import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { User, Truck, UserPlus, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from '../components/ui/alert';

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [activeTab, setActiveTab] = useState('giris');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Floating tire positions
  const tirePositions = [
    { top: '5%', left: '5%', size: 80, opacity: 0.15, delay: 0 },
    { top: '15%', right: '8%', size: 120, opacity: 0.12, delay: 1 },
    { top: '60%', left: '3%', size: 100, opacity: 0.1, delay: 2 },
    { top: '75%', right: '5%', size: 90, opacity: 0.13, delay: 0.5 },
    { top: '40%', left: '8%', size: 70, opacity: 0.08, delay: 1.5 },
    { top: '25%', right: '15%', size: 60, opacity: 0.1, delay: 2.5 },
    { bottom: '10%', left: '15%', size: 110, opacity: 0.11, delay: 0.8 },
    { bottom: '20%', right: '12%', size: 85, opacity: 0.09, delay: 1.8 },
  ];

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e, loginType) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    const result = await login(formData.identifier, formData.password, loginType);
    
    setIsLoading(false);
    if (result.success) {
      if (result.user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 overflow-hidden relative">
      {/* Background Image - Tire Warehouse */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_tireflow-1/artifacts/iooqf56k_lastik360.webp')`,
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/60" />
      
      <div className="w-full max-w-md relative z-10">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-36 h-36 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_tireflow-1/artifacts/k32sxqbq_LAST%C4%B0K360.png" 
              alt="Lastik360 Logo" 
              className="w-full h-full object-contain drop-shadow-2xl"
            />
          </div>
          <h1 className="text-2xl font-bold text-white drop-shadow-lg">TÜRKİYE'NİN LASTİK HAVUZU</h1>
        </div>

        <Card className="backdrop-blur-xl bg-white/95 shadow-2xl border-0">
          <CardHeader className="space-y-1 pb-4">
            <CardTitle className="text-xl text-center text-gray-800">Hoş Geldiniz</CardTitle>
            <CardDescription className="text-center">Devam etmek için giriş yapın</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-3 mb-6 bg-gray-100">
                <TabsTrigger value="giris" className="text-xs sm:text-sm data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  <User className="w-4 h-4 mr-1" />
                  Giriş Yap
                </TabsTrigger>
                <TabsTrigger value="toptanci" className="text-xs sm:text-sm data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  <Truck className="w-4 h-4 mr-1" />
                  Toptancı
                </TabsTrigger>
                <TabsTrigger value="uyelik" className="text-xs sm:text-sm data-[state=active]:bg-blue-600 data-[state=active]:text-white">
                  <UserPlus className="w-4 h-4 mr-1" />
                  Yeni Üyelik
                </TabsTrigger>
              </TabsList>

              {/* Normal Login */}
              <TabsContent value="giris">
                <form onSubmit={(e) => handleSubmit(e, 'normal')} className="space-y-4">
                  {error && (
                    <Alert variant="destructive" className="bg-red-50 border-red-200">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="identifier">E-posta veya Kullanıcı Adı</Label>
                    <Input
                      id="identifier"
                      name="identifier"
                      type="text"
                      placeholder="ornek@email.com"
                      value={formData.identifier}
                      onChange={handleInputChange}
                      className="h-11"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Şifre</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="h-11 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <Link to="/forgot-password" className="text-sm text-blue-600 hover:underline">
                      Şifremi unuttum
                    </Link>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-blue-600 hover:bg-blue-700 transition-all duration-200"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                  </Button>
                </form>
              </TabsContent>

              {/* Toptanci Login */}
              <TabsContent value="toptanci">
                <form onSubmit={(e) => handleSubmit(e, 'toptanci')} className="space-y-4">
                  {error && (
                    <Alert variant="destructive" className="bg-red-50 border-red-200">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-amber-800">
                      <Truck className="w-4 h-4 inline mr-1" />
                      Bu alan sadece toptancı üyeler içindir.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="identifier-toptanci">E-posta veya Kullanıcı Adı</Label>
                    <Input
                      id="identifier-toptanci"
                      name="identifier"
                      type="text"
                      placeholder="toptanci@email.com"
                      value={formData.identifier}
                      onChange={handleInputChange}
                      className="h-11"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password-toptanci">Şifre</Label>
                    <div className="relative">
                      <Input
                        id="password-toptanci"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={formData.password}
                        onChange={handleInputChange}
                        className="h-11 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full h-11 bg-amber-600 hover:bg-amber-700 transition-all duration-200"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Giriş yapılıyor...' : 'Toptancı Girişi'}
                  </Button>
                </form>
              </TabsContent>

              {/* New Membership */}
              <TabsContent value="uyelik">
                <div className="space-y-4">
                  <p className="text-sm text-gray-600 text-center">
                    Üyelik türünüzü seçin ve kayıt olun
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <Link to="/register/bireysel">
                      <Card className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-blue-400 h-full">
                        <CardContent className="p-6 text-center">
                          <User className="w-12 h-12 mx-auto mb-3 text-blue-600" />
                          <h3 className="font-semibold text-gray-800">Bireysel Üyelik</h3>
                          <p className="text-xs text-gray-500 mt-1">Perakende alım için</p>
                        </CardContent>
                      </Card>
                    </Link>
                    <Link to="/register/toptanci">
                      <Card className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:border-amber-400 h-full">
                        <CardContent className="p-6 text-center">
                          <Truck className="w-12 h-12 mx-auto mb-3 text-amber-600" />
                          <h3 className="font-semibold text-gray-800">Toptancı Üyelik</h3>
                          <p className="text-xs text-gray-500 mt-1">Toptan alım için</p>
                        </CardContent>
                      </Card>
                    </Link>
                  </div>
                  <p className="text-xs text-gray-400 text-center mt-4">
                    * Üyelikler admin onayı sonrası aktif olur
                  </p>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6">
          {/* Social Links */}
          <div className="flex items-center justify-center gap-4 mb-3">
            <a 
              href="https://www.instagram.com/lastik360/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white hover:text-pink-400 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
              <span className="text-sm font-medium">lastik360</span>
            </a>
            <a 
              href="https://wa.me/+447441931663" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-white hover:text-green-400 transition-colors"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              <span className="text-sm font-medium">WhatsApp</span>
            </a>
          </div>
          <p className="text-blue-200 text-xs">
            © 2024 Türkiye'nin Lastik Havuzu
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
